import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles, withTheme } from '@material-ui/core';
import React, { useState } from 'react';

import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { flattenVendorContacts } from '../details/utils';

import Spinner from 'modules/common/spinner.component';
import {
  deleteVendorContact,
  getVendorContacts,
  postVendorContact,
  updateVendorContact
} from 'modules/authorizations/store/actions/dataManagement.actions';
import {
  DELETE_VENDOR_CONTACT_FAILURE,
  POST_VENDOR_CONTACT_FAILURE,
  UPDATE_VENDOR_CONTACT_FAILURE
} from 'modules/authorizations/store/types/dataManagementTypes';
import ContactsTable from './contactsTable.component';
import { PrefixOptions } from 'modules/authorizations/details/Employee/FormFields/formFieldConstants';
import { sortByTitle, sortCountries, USA_COUNTRY_ID } from 'modules/common/utils';

const styles = theme => {
  return {
    row: {
      backgroundColor: theme.palette.common.white
    },
    accordionStyleContacts: {
      maxWidth: 1050
    },
    inputGroup: {
      display: 'inline-flex',
      justifyContent: 'space-between'
    },
    root: {
      maxWidth: 170,
      marginRight: 10
    },
    extRoot: {
      width: 115,
      marginRight: 10
    },
    primaryContactRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%'
    },
    primaryText: {
      paddingTop: 12,
      paddingRight: 5
    },
    newRow: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 1050
    },
    tabBorder: {
      borderLeft: `2px solid ${theme.palette.grey[400]}`,
      marginLeft: theme.spacing(2)
    }
  };
};

const ContactsCard = props => {
  const { hideEdit, vendorId, vendorContacts, enumerations } = props;
  const [isLoading, setIsLoading] = useState(false);
  
  const provinces =
    enumerations && enumerations.provinces && enumerations.provinces.length > 0
      ? sortByTitle(enumerations.provinces.filter(s => s.isActive === true && s.title))
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const countries =
    enumerations && enumerations.countries && enumerations.countries.length > 0
      ? sortCountries(enumerations.countries.filter(s => s.isActive === true && s.title))
      : [{ id: USA_COUNTRY_ID, title: 'United States' }];

  const adjustedColumns = [
    { accessorKey: 'prefix', header: 'Prefix', type: 'dropdown', options: PrefixOptions, required: false, hideInTable: true },
    { accessorKey: 'firstName', header: 'First Name', type: 'text', required: true, },
    { accessorKey: 'lastName', header: 'Last Name', type: 'text', required: true, },
    { accessorKey: 'phoneNumber', header: 'Phone', type: 'text', required: false, },
    { accessorKey: 'email', header: 'Email', type: 'text', required: false, size: 50 },
    { accessorKey: 'description', header: 'Description', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'jobTitle', header: 'Job Title', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'isPrimary', header: 'Primary Contact', type: 'checkbox', required: false, hideInTable: true },
    { accessorKey: 'addressLine1', header: 'Address Line 1', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'addressLine2', header: 'Address Line 2', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'addressLine3', header: 'Address Line 3', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'city', header: 'City', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'county', header: 'County', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'countryId', header: 'Country', type: 'dropdown-v2', options: countries, required: false, hideInTable: true },
    { accessorKey: 'provinceId', header: 'State', type: 'dropdown-v2', options: provinces, parentField: 'countryId', parentOptionKey: 'countryId', required: false, hideInTable: true },
    { accessorKey: 'zipcode', header: 'Zipcode', type: 'text', required: false, hideInTable: false }
  ];

  const updateRow = async (record, values) => {
    setIsLoading(true);
    const existing = vendorContacts.find(c => c.contact.contactId === record.original.contactId);
    const newValues = { contact: { ...existing.contact }, contactAddress: { ...existing.contactAddress } };
    newValues.contact.prefix = values.prefix ? values.prefix : 'N/a';
    newValues.contact.firstName = values.firstName;
    newValues.contact.lastName = values.lastName;
    newValues.contact.phoneNumber = values.phoneNumber;
    newValues.contact.email = values.email;
    newValues.contact.description = values.description;
    newValues.contact.jobTitle = values.jobTitle;
    newValues.contact.isPrimary = values.isPrimary === true;
    newValues.contactAddress.addressLine1 = values.addressLine1;
    newValues.contactAddress.addressLine2 = values.addressLine2;
    newValues.contactAddress.addressLine3 = values.addressLine3;
    newValues.contactAddress.city = values.city;
    newValues.contactAddress.county = values.county;
    newValues.contactAddress.countryId = values.countryId ? values.countryId : '00000000-0000-0000-0000-000000000000';
    newValues.contactAddress.provinceId = values.provinceId ? values.provinceId : '00000000-0000-0000-0000-000000000000';
    newValues.contactAddress.zipcode = values.zipcode;
    newValues.contactAddress.modifiedBy = 1;
    newValues.contactAddress.modifiedOn = new Date();

    const resp = await props.updateVendorContact(newValues);
    if (resp.type === UPDATE_VENDOR_CONTACT_FAILURE) {
      setIsLoading(false);
      return false;
    } else {
      setIsLoading(false);
        return true;
    }
  };

  const createRow = async values => {
    const newValues = { 
      contact: {
        vendorId: vendorId,
        prefix: values.prefix,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        description: values.description,
        jobTitle: values.jobTitle,
        isPrimary: values.isPrimary ? values.isPrimary : false,
        addedBy: 1,
        addedOn: new Date(),
        modifiedBy: 1,
        modifiedOn: new Date()
      },
      contactAddress: {
        addressTypeId: 'b530b48e-6f25-4356-8d7b-70efb5a8005e',
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        addressLine3: values.addressLine3,
        city: values.city,
        county: values.county,
        countryId: values.countryId ? values.countryId : '00000000-0000-0000-0000-000000000000',
        provinceId: values.provinceId ? values.provinceId : '00000000-0000-0000-0000-000000000000',
        zipcode: values.zipcode,
        addedBy: 1,
        addedOn: new Date(),
        modifiedBy: 1,
        modifiedOn: new Date()
      }
     };

    setIsLoading(true);
    values.vendorId = vendorId;
    const resp = await props.postVendorContact(newValues);
    if (resp.type === POST_VENDOR_CONTACT_FAILURE) {
      setIsLoading(false);
      return false;
    } else {
       setIsLoading(false);
        return true;
    }
  };

  const deleteRow = async record => {
    setIsLoading(true);
    const id = record.original.contactId;
    const resp = await props.deleteVendorContact(id);
    if (resp.type === DELETE_VENDOR_CONTACT_FAILURE) {
      setIsLoading(false);
      return false;
    } else {
       setIsLoading(false);
        return true;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Tooltip title={'Moving Dates'} key={'vendorContacts'} placement="right">
            <FontAwesomeIcon icon={['fas', 'user-friends']} size="2x" />
          </Tooltip>
          <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
            Contacts
          </Typography>
        </Box>
        {!isLoading && (
          <ContactsTable
            editingMode={'modal'}
            columns={adjustedColumns}
            rows={flattenVendorContacts(vendorContacts)}
            updateRow={updateRow}
            createRow={createRow}
            deleteRow={deleteRow}
            customAddText={'Add Contact'}
            hideEdit={hideEdit}
            countriesEnum={countries}
            provincesEnum={provinces}
          />
        )}
        {isLoading && <Spinner />}
      </CardContent>
    </Card>
  );
};

export default compose(
  withStyles(styles),
  withTheme,
  connect(null, {
    getVendorContacts,
    deleteVendorContact,
    postVendorContact,
    updateVendorContact
  })
)(ContactsCard);
